import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withTranslation } from 'react-i18next';
import _ from 'lodash';

import { Check, ChevronDown, ChevronRight, ThLarge } from 'common/images/FaIcons';
import { canManageOrganisations, canAccessOrgDashboard } from 'common/rbac/util';

import { userActions } from '../../onboarding/user.actions';
import { orgContextActions } from '../Organisation/orgContext';
import { getOrgDashboardPath } from '../Organisation/orgRouteUtils';
import { buildOrgSiteTree } from '../Organisation/utils/orgSiteTree';
import { history } from '../../common/helpers';
import BreadcrumbSearch from './BreadcrumbSearch';
import * as S from './OrgSiteBreadcrumb.styled';

// Number of sites above which the site dropdown gets a search box. Kept low so
// the search option is available even for orgs with only a handful of sites.
const SITE_SEARCH_THRESHOLD = 1;
const MANAGE_ORGS_PATH = '/admin-dashboard/organizations';

// Filters the org→site tree by a search term. An org matches wholesale when its
// name matches; otherwise only its matching sites are kept. Org-less flat sites
// are filtered by name.
function filterTree(tree, rawSearch) {
  const search = (rawSearch || '').trim().toLowerCase();
  if (!search) return { orgs: tree.orgs, flatSites: tree.flatSites };

  const orgs = [];
  tree.orgs.forEach((org) => {
    const orgMatches = (org.orgName || '').toLowerCase().includes(search);
    const sites = orgMatches
      ? org.sites
      : org.sites.filter(site => (site.name || '').toLowerCase().includes(search));
    if (orgMatches || sites.length > 0) orgs.push({ ...org, sites });
  });
  const flatSites = tree.flatSites.filter(site => (site.name || '').toLowerCase().includes(search));
  return { orgs, flatSites };
}

// Org + site breadcrumb inside the asset-hierarchy panel. Reuses the top-bar
// SitesDropdown's account_switcher data and selection flow, unsaved-summary
// guard included.
class OrgSiteBreadcrumb extends React.Component {
  constructor() {
    super();
    this.state = {
      orgOpen: false,
      siteOpen: false,
      orgSearch: '',
      siteSearch: '',
      // Set of orgId values the user has manually expanded in the switcher.
      // Populated on first open: active org is expanded by default, others collapsed.
      expandedOrgs: null
    };
    // Deferred navigation held while the unsaved-summary prompt is up.
    this.pendingOrgNavigation = null;
    this.pendingAccount = null;
    this.pendingLeaveOrgDashboard = false;
  }

  componentDidUpdate(prevProps) {
    // User confirmed discarding the unsaved analyst summary: run whatever
    // navigation we were holding back.
    if (
      !_.isEqual(prevProps.discardAnalystSummary, this.props.discardAnalystSummary) &&
      this.props.discardAnalystSummary
    ) {
      if (this.pendingOrgNavigation) {
        const org = this.pendingOrgNavigation;
        this.pendingOrgNavigation = null;
        this.completeOrgNavigation(org);
        this.setDiscardAnalystSummary(false);
        return;
      }
      if (this.pendingLeaveOrgDashboard) {
        this.pendingLeaveOrgDashboard = false;
        this.completeLeaveOrgDashboard();
        this.setDiscardAnalystSummary(false);
        return;
      }
      if (this.pendingAccount && this.pendingAccount !== this.getCurrentAccountId()) {
        this.props.selectAccount(this.pendingAccount);
        this.pendingAccount = null;
        this.setDiscardAnalystSummary(false);
      }
    }

    // Prompt dismissed without discarding: drop any held navigation.
    if (
      prevProps.analystSummaryWarning &&
      !this.props.analystSummaryWarning &&
      !this.props.discardAnalystSummary
    ) {
      this.pendingOrgNavigation = null;
      this.pendingAccount = null;
      this.pendingLeaveOrgDashboard = false;
    }
  }

  getUser = () => (this.props.user ? this.props.user.user : null);

  getCurrentAccountId = () => {
    const user = this.getUser();
    return user ? user.account_id : null;
  };

  getTree = () => {
    const user = this.getUser();
    const items = user && user.account_switcher && Array.isArray(user.account_switcher.items)
      ? user.account_switcher.items
      : [];
    return buildOrgSiteTree(items);
  };

  resolveActiveOrg = (tree, activeOrgId) => {
    if (activeOrgId == null) return null;

    const fromTree = tree.orgs.find(org => org.orgId === activeOrgId);
    if (fromTree) return fromTree;

    const { orgContext, t } = this.props;
    const orgName = (orgContext && orgContext.id === activeOrgId && orgContext.name)
      ? orgContext.name
      : t('orgView.breadcrumb.unknownOrg');

    return {
      orgId: activeOrgId,
      orgName,
      sites: []
    };
  };

  // The org (and its site) that the current account belongs to.
  resolveCurrent = (tree, accountId) => {
    let currentOrg = null;
    let currentSite = null;
    tree.orgs.forEach((org) => {
      org.sites.forEach((site) => {
        if (site.id === accountId) {
          currentOrg = org;
          currentSite = site;
        }
      });
    });
    if (!currentSite) {
      currentSite = tree.flatSites.find(site => site.id === accountId) || null;
    }
    return { currentOrg, currentSite };
  };

  getFallbackSiteName = (accountId) => {
    const user = this.getUser();
    const accounts = user && user.accounts ? user.accounts : [];
    const match = accounts.find(acc => acc.id === accountId);
    return match ? match.name : '';
  };

  // Returns true when the caller should defer because there is an unsaved
  // analyst summary and the user must confirm discarding it first.
  needsSummaryConfirmation = () => {
    const { checkNewSummaryExists, setAnalystSummaryWarning } = this.props;
    if (checkNewSummaryExists && checkNewSummaryExists()) {
      if (setAnalystSummaryWarning) setAnalystSummaryWarning(true);
      return true;
    }
    return false;
  };

  setDiscardAnalystSummary = (value) => {
    if (this.props.setDiscardAnalystSummary) this.props.setDiscardAnalystSummary(value);
  };

  completeOrgNavigation = (org) => {
    if (!org || org.orgId == null) return;
    if (!canAccessOrgDashboard(this.getUser(), org.orgId)) return;
    this.props.setOrgContext({ id: org.orgId, name: org.orgName });
    this.setState({ orgOpen: false });
    history.push(getOrgDashboardPath(org.orgId));
  };

  completeLeaveOrgDashboard = () => {
    this.setState({ siteOpen: false });
    this.props.clearOrgContext();
    history.push('/');
  };

  handleOrgClick = (org) => {
    if (!org || org.orgId == null) return;
    if (!canAccessOrgDashboard(this.getUser(), org.orgId)) return;
    if (this.needsSummaryConfirmation()) {
      this.pendingOrgNavigation = org;
      return;
    }
    this.completeOrgNavigation(org);
  };

  handleSiteClick = (siteId) => {
    this.setState({ siteOpen: false });
    if (siteId === this.getCurrentAccountId()) {
      // Already this account: from an org dashboard just leave the org view; on a
      // site dashboard we're already there.
      if (this.props.activeOrgId != null) {
        if (this.needsSummaryConfirmation()) {
          this.pendingLeaveOrgDashboard = true;
          return;
        }
        this.completeLeaveOrgDashboard();
      }
      return;
    }
    if (this.needsSummaryConfirmation()) {
      this.pendingAccount = siteId;
      return;
    }
    this.props.selectAccount(siteId);
  };

  // Resolve which org should be expanded by default (active site's org, or
  // active org on the org dashboard).
  getDefaultExpandedOrg = (tree, accountId, activeOrgId) => {
    if (activeOrgId != null) return activeOrgId;
    let found = null;
    tree.orgs.forEach((org) => {
      org.sites.forEach((site) => {
        if (site.id === accountId) found = org.orgId;
      });
    });
    return found;
  };

  toggleOrg = () => {
    this.setState((prev) => {
      const opening = !prev.orgOpen;
      if (opening && prev.expandedOrgs === null) {
        // First open: seed with the active org expanded, all others collapsed.
        const tree = this.getTree();
        const accountId = this.getCurrentAccountId();
        const { activeOrgId } = this.props;
        const defaultId = this.getDefaultExpandedOrg(tree, accountId, activeOrgId);
        return {
          orgOpen: true,
          orgSearch: '',
          expandedOrgs: defaultId != null ? new Set([defaultId]) : new Set()
        };
      }
      return { orgOpen: opening, orgSearch: '' };
    });
  };

  toggleSite = () => this.setState(prev => ({ siteOpen: !prev.siteOpen, siteSearch: '' }));

  toggleExpandOrg = (e, orgId) => {
    e.stopPropagation();
    this.setState((prev) => {
      const next = new Set(prev.expandedOrgs || []);
      if (next.has(orgId)) { next.delete(orgId); } else { next.add(orgId); }
      return { expandedOrgs: next };
    });
  };

  handleManageOrgs = () => {
    this.setState({ orgOpen: false });
    history.push(MANAGE_ORGS_PATH);
  };

  // Orgs only — switching sites is the site pill's job. Keeps the breadcrumb a
  // "go to a place" control, not a second global site switcher.
  renderOrgList = (tree, selectedOrgId) => {
    const { t } = this.props;
    const search = (this.state.orgSearch || '').trim().toLowerCase();
    const orgs = search
      ? tree.orgs.filter(org => (org.orgName || '').toLowerCase().includes(search))
      : tree.orgs;
    const canManage = canManageOrganisations(this.getUser());
    const user = this.getUser();
    // Nothing to search through with a single org (e.g. a one-org admin who only
    // opened this dropdown to reach "Manage organisations & sites").
    const showSearch = tree.orgs.length > 1;

    return (
      <>
        {showSearch && (
          <BreadcrumbSearch
            value={this.state.orgSearch}
            onChange={val => this.setState({ orgSearch: val })}
            onClear={() => this.setState({ orgSearch: '' })}
            placeholder={t('orgView.breadcrumb.searchOrgs', { defaultValue: 'Search organisations' })}
            autoFocus
          />
        )}
        {orgs.length === 0 && (
          <S.EmptyMsg>{t('orgView.breadcrumb.noOrgsFound', { defaultValue: 'No organisations found' })}</S.EmptyMsg>
        )}
        {orgs.map((org) => {
          const clickable = org.orgId != null && canAccessOrgDashboard(user, org.orgId);
          const isSelected = selectedOrgId != null && org.orgId === selectedOrgId;
          return (
            <S.OrgRow
              key={`org-${org.orgId}`}
              selected={isSelected}
              clickable={clickable}
              role={clickable ? 'button' : undefined}
              aria-current={isSelected ? 'true' : undefined}
              tabIndex={clickable ? 0 : undefined}
              onClick={clickable ? () => this.handleOrgClick(org) : undefined}
              onKeyDown={clickable ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.handleOrgClick(org); }
              } : undefined}
            >
              <ThLarge size="sm" className="org-icon" />
              <span className="text">{org.orgName}</span>
              <S.Count>{org.sites.length}</S.Count>
              {isSelected && <S.CheckMark><Check size="xs" /></S.CheckMark>}
            </S.OrgRow>
          );
        })}
        {canManage && (
          <S.ManageFooter
            role="button"
            tabIndex={0}
            onClick={this.handleManageOrgs}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.handleManageOrgs(); }
            }}
          >
            <span>{t('orgView.breadcrumb.manageOrgsAndSites')}</span>
          </S.ManageFooter>
        )}
      </>
    );
  };

  // Full org→site tree. Retained only for the org-less-account fallback
  // (renderGlobalSitePill), where there's no org pill and the global tree is the
  // only way to navigate out. The org pill itself uses renderOrgList (Option B).
  renderTreeBody = (tree, accountId, selectedOrgId) => {
    const { t } = this.props;
    const searching = (this.state.orgSearch || '').trim().length > 0;
    const { orgs, flatSites } = filterTree(tree, this.state.orgSearch);
    const empty = orgs.length === 0 && flatSites.length === 0;
    const canManage = canManageOrganisations(this.getUser());
    const user = this.getUser();
    // While searching, expand all matching orgs so results are visible.
    const expandedOrgs = this.state.expandedOrgs || new Set();

    return (
      <>
        <BreadcrumbSearch
          value={this.state.orgSearch}
          onChange={val => this.setState({ orgSearch: val })}
          onClear={() => this.setState({ orgSearch: '' })}
          placeholder={t('orgView.breadcrumb.searchOrgsAndSites')}
          autoFocus
        />
        {empty && <S.EmptyMsg>{t('orgView.breadcrumb.noOrgsOrSitesFound')}</S.EmptyMsg>}
        {orgs.map((org) => {
          const isExpanded = searching || expandedOrgs.has(org.orgId);
          const clickable = org.orgId != null && canAccessOrgDashboard(user, org.orgId);
          const isSelected = selectedOrgId != null && org.orgId === selectedOrgId;
          return (
            <React.Fragment key={`org-${org.orgId}`}>
              <S.OrgGroupHeader
                clickable={clickable}
                selected={isSelected}
              >
                {/* Chevron expands/collapses the site list — separate from the navigate click */}
                <S.OrgCaret
                  hidden={org.sites.length === 0}
                  role="button"
                  tabIndex={org.sites.length === 0 ? undefined : 0}
                  aria-expanded={isExpanded}
                  aria-label={isExpanded
                    ? t('orgView.breadcrumb.collapseOrg', { defaultValue: 'Collapse {{org}}', org: org.orgName })
                    : t('orgView.breadcrumb.expandOrg', { defaultValue: 'Expand {{org}}', org: org.orgName })}
                  onClick={e => this.toggleExpandOrg(e, org.orgId)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.toggleExpandOrg(e, org.orgId); }
                  }}
                >
                  {isExpanded ? <ChevronDown /> : <ChevronRight />}
                </S.OrgCaret>
                <ThLarge size="sm" className="org-icon" />
                {clickable ? (
                  <span
                    className="name"
                    role="button"
                    tabIndex={0}
                    onClick={() => this.handleOrgClick(org)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.handleOrgClick(org); }
                    }}
                  >
                    {org.orgName}
                  </span>
                ) : (
                  <span className="name">{org.orgName}</span>
                )}
                <S.Count>{org.sites.length}</S.Count>
              </S.OrgGroupHeader>
              {isExpanded && org.sites.map(site => this.renderSiteRow(site, accountId))}
            </React.Fragment>
          );
        })}
        {flatSites.length > 0 && <S.SectionLabel>{t('orgView.otherSites')}</S.SectionLabel>}
        {flatSites.map(site => this.renderSiteRow(site, accountId))}
        {canManage && (
          <S.ManageFooter
            role="button"
            tabIndex={0}
            onClick={this.handleManageOrgs}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.handleManageOrgs(); }
            }}
          >
            <span>{t('orgView.breadcrumb.manageOrgsAndSites')}</span>
          </S.ManageFooter>
        )}
      </>
    );
  };

  // Role-gated one-click link to the org dashboard. stopPropagation keeps the
  // click off the pill's dropdown toggle; the chevron opens the switcher.
  renderOrgPillInner = (org) => {
    const { t } = this.props;
    const clickable = org.orgId != null && canAccessOrgDashboard(this.getUser(), org.orgId);
    if (!clickable) {
      return (
        <>
          <S.PillIcon><ThLarge size="xs" /></S.PillIcon>
          <S.PillText className="pill-name">{org.orgName}</S.PillText>
        </>
      );
    }
    const openDashboard = (e) => {
      e.stopPropagation();
      this.handleOrgClick(org);
    };
    return (
      <S.PillLink
        role="link"
        tabIndex={0}
        title={t('orgView.breadcrumb.openOrgDashboard', { defaultValue: 'Open {{org}} dashboard', org: org.orgName })}
        onClick={openDashboard}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openDashboard(e); }
        }}
      >
        <S.PillIcon><ThLarge size="xs" /></S.PillIcon>
        <S.PillText className="pill-name">{org.orgName}</S.PillText>
      </S.PillLink>
    );
  };

  // Org segment. A switcher dropdown only when there's more than one org to pick;
  // a single org collapses to a pure link (or a plain label without dashboard access)
  // so we don't dangle a redundant menu.
  renderOrgControl = (currentOrg, accountId, tree) => {
    const multiOrg = tree.orgs.length > 1;
    // Gate on role, not org count: the dropdown also carries "Manage
    // organisations & sites", which one-org admins still need.
    const showDropdown = multiOrg || canManageOrganisations(this.getUser());
    if (showDropdown) {
      return (
        <S.OrgFilter
          id="org-breadcrumb"
          open={this.state.orgOpen}
          toggleOpen={this.toggleOrg}
          alignHorizontal="left"
          marginTop="7px"
          zIndex={1000}
          customDisplayValue={() => (
            <S.Pill title={currentOrg.orgName}>
              {this.renderOrgPillInner(currentOrg)}
              <S.PillChevron><ChevronDown /></S.PillChevron>
            </S.Pill>
          )}
        >
          {this.renderOrgList(tree, currentOrg.orgId)}
        </S.OrgFilter>
      );
    }
    const clickable = currentOrg.orgId != null && canAccessOrgDashboard(this.getUser(), currentOrg.orgId);
    return (
      <S.OrgStatic id="org-breadcrumb">
        {clickable ? (
          <S.Pill title={currentOrg.orgName}>
            {this.renderOrgPillInner(currentOrg)}
          </S.Pill>
        ) : (
          <S.PillLabel title={currentOrg.orgName}>
            <S.PillIcon><ThLarge size="xs" /></S.PillIcon>
            <S.PillText className="pill-name">{currentOrg.orgName}</S.PillText>
          </S.PillLabel>
        )}
      </S.OrgStatic>
    );
  };

  // Site segment. Dropdown only when there's more than one site to switch to;
  // otherwise a plain label — a single-site user gets no dead menu.
  renderSiteControl = (sites, accountId, siteName) => {
    if (sites.length > 1) {
      return this.renderSitePill(sites, accountId, siteName);
    }
    return (
      <S.SiteStatic>
        <S.PillLabel title={siteName}>
          <S.PillText className="pill-name">{siteName}</S.PillText>
        </S.PillLabel>
      </S.SiteStatic>
    );
  };

  // Site segment on an org dashboard. account_id survives org navigation, so
  // `lastSite` is where the user came from — shown muted with a one-click return.
  // No return site (landed straight on the org) → the plain CTA label.
  renderReturnSiteControl = (sites, lastSite, placeholder) => {
    const { t } = this.props;
    if (!lastSite) {
      return (
        <S.SiteStatic>
          <S.PillLabel title={placeholder}>
            <S.PillText className="pill-name" muted>{placeholder}</S.PillText>
          </S.PillLabel>
        </S.SiteStatic>
      );
    }

    const returnToSite = (e) => { e.stopPropagation(); this.handleSiteClick(lastSite.id); };
    const nameLink = (
      <S.PillLink
        role="link"
        tabIndex={0}
        title={t('orgView.breadcrumb.returnToSite', { defaultValue: 'Return to {{site}}', site: lastSite.name })}
        onClick={returnToSite}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); returnToSite(e); }
        }}
      >
        <S.PillText className="pill-name" muted>{lastSite.name}</S.PillText>
      </S.PillLink>
    );

    // Only the one site to return to: a muted pill with no switcher chevron.
    if (sites.length <= 1) {
      return (
        <S.SiteStatic>
          <S.Pill muted title={lastSite.name}>{nameLink}</S.Pill>
        </S.SiteStatic>
      );
    }

    // Multiple sites: name returns; chevron opens the sibling-site switcher, with
    // the return site checked (accountId === lastSite.id).
    return (
      <S.SiteFilter
        id="site-breadcrumb"
        open={this.state.siteOpen}
        toggleOpen={this.toggleSite}
        alignHorizontal="left"
        marginTop="7px"
        zIndex={1000}
        customDisplayValue={() => (
          <S.Pill muted title={lastSite.name}>
            {nameLink}
            <S.PillChevron><ChevronDown /></S.PillChevron>
          </S.Pill>
        )}
      >
        {this.renderSiteDropdownBody(sites, lastSite.id)}
      </S.SiteFilter>
    );
  };

  renderGlobalSitePill = (siteName, accountId, tree) => (
    <S.SiteFilter
      id="site-breadcrumb"
      open={this.state.orgOpen}
      toggleOpen={this.toggleOrg}
      alignHorizontal="left"
      marginTop="7px"
      zIndex={1000}
      customDisplayValue={() => (
        <S.Pill title={siteName}>
          <S.PillText>{siteName}</S.PillText>
          <S.PillChevron><ChevronDown /></S.PillChevron>
        </S.Pill>
      )}
    >
      {this.renderTreeBody(tree, accountId, null)}
    </S.SiteFilter>
  );

  renderSiteRow = (site, accountId) => {
    const selected = site.id === accountId;
    return (
      <S.SiteRow
        key={`site-${site.id}`}
        selected={selected}
        role="button"
        aria-current={selected ? 'true' : undefined}
        tabIndex={0}
        onClick={() => this.handleSiteClick(site.id)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.handleSiteClick(site.id); }
        }}
      >
        <span className="text">{site.name}</span>
        {selected && <S.CheckMark><Check size="xs" /></S.CheckMark>}
      </S.SiteRow>
    );
  };

  // Search box (above a threshold) + the site rows. Shared by the plain site
  // switcher and the org-level return-context switcher.
  renderSiteDropdownBody = (sites, accountId) => {
    const { t } = this.props;
    const search = this.state.siteSearch.trim().toLowerCase();
    const filtered = search
      ? sites.filter(site => (site.name || '').toLowerCase().includes(search))
      : sites;
    const showSearch = sites.length > SITE_SEARCH_THRESHOLD;

    return (
      <>
        {showSearch && (
          <BreadcrumbSearch
            value={this.state.siteSearch}
            onChange={val => this.setState({ siteSearch: val })}
            onClear={() => this.setState({ siteSearch: '' })}
            placeholder={t('orgView.breadcrumb.searchSites')}
            autoFocus
          />
        )}
        {filtered.length === 0 ? (
          <S.EmptyMsg>{t('orgView.breadcrumb.noSitesFound')}</S.EmptyMsg>
        ) : (
          filtered.map(site => this.renderSiteRow(site, accountId))
        )}
      </>
    );
  };

  renderSitePill = (sites, accountId, siteName) => (
    <S.SiteFilter
      id="site-breadcrumb"
      open={this.state.siteOpen}
      toggleOpen={this.toggleSite}
      alignHorizontal="left"
      marginTop="7px"
      zIndex={1000}
      customDisplayValue={() => (
        <S.Pill title={siteName}>
          <S.PillText>{siteName}</S.PillText>
          <S.PillChevron><ChevronDown /></S.PillChevron>
        </S.Pill>
      )}
    >
      {this.renderSiteDropdownBody(sites, accountId)}
    </S.SiteFilter>
  );

  render() {
    const { t, activeOrgId } = this.props;
    const tree = this.getTree();
    const accountId = this.getCurrentAccountId();
    const { currentOrg, currentSite } = this.resolveCurrent(tree, accountId);

    // Pills reflect the routed org, not the last-selected account. At org level
    // no site is current, so nothing is checked (null selected-account id).
    if (activeOrgId != null) {
      const activeOrg = this.resolveActiveOrg(tree, activeOrgId);
      if (activeOrg) {
        // The site the user was last on, if it belongs to the org being viewed —
        // shown as return context instead of an empty "Select a site" CTA.
        const lastSite = activeOrg.sites.find(site => site.id === accountId) || null;
        return (
          <S.PillRow role="navigation" aria-label={t('orgView.breadcrumb.navLabel', { defaultValue: 'Organisation and site' })}>
            {this.renderOrgControl(activeOrg, null, tree)}
            <S.Separator>›</S.Separator>
            {this.renderReturnSiteControl(activeOrg.sites, lastSite, t('orgView.breadcrumb.selectSite'))}
          </S.PillRow>
        );
      }
    }

    const showOrgSegment = tree.orgs.length > 0 && currentOrg != null;
    const siteName = currentSite ? currentSite.name : this.getFallbackSiteName(accountId);
    const sites = currentOrg ? currentOrg.sites : tree.flatSites;

    if (!showOrgSegment && !siteName) return null;

    // Org-less account while other orgs exist: fall back to the full-tree
    // switcher on the site segment so switching elsewhere stays easy.
    const orgless = !showOrgSegment && tree.orgs.length > 0;

    return (
      <S.PillRow role="navigation" aria-label={t('orgView.breadcrumb.navLabel', { defaultValue: 'Organisation and site' })}>
        {showOrgSegment && (
          <>
            {this.renderOrgControl(currentOrg, accountId, tree)}
            <S.Separator>›</S.Separator>
          </>
        )}
        {orgless
          ? this.renderGlobalSitePill(siteName, accountId, tree)
          : (siteName && this.renderSiteControl(sites, accountId, siteName))}
      </S.PillRow>
    );
  }
}

OrgSiteBreadcrumb.propTypes = {
  user: PropTypes.object,
  orgContext: PropTypes.object,
  activeOrgId: PropTypes.number,
  selectAccount: PropTypes.func.isRequired,
  setOrgContext: PropTypes.func.isRequired,
  clearOrgContext: PropTypes.func.isRequired,
  checkNewSummaryExists: PropTypes.func,
  setAnalystSummaryWarning: PropTypes.func,
  setDiscardAnalystSummary: PropTypes.func,
  discardAnalystSummary: PropTypes.bool,
  analystSummaryWarning: PropTypes.bool,
  t: PropTypes.func.isRequired
};

OrgSiteBreadcrumb.defaultProps = {
  user: null,
  orgContext: null,
  activeOrgId: null,
  checkNewSummaryExists: null,
  setAnalystSummaryWarning: null,
  setDiscardAnalystSummary: null,
  discardAnalystSummary: false,
  analystSummaryWarning: false
};

// Unconnected class exported under a distinct name for shallow-render tests
// (avoids import/no-named-as-default at the default-import sites).
export { OrgSiteBreadcrumb as OrgSiteBreadcrumbBase };

const mapStateToProps = state => ({
  user: state.user,
  orgContext: state.orgContext
});

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      selectAccount: userActions.selectAccount,
      setOrgContext: orgContextActions.setOrgContext,
      clearOrgContext: orgContextActions.clearOrgContext
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(OrgSiteBreadcrumb));
